import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { createZodFormResolver, lt, showSuccessAlert, useFeatureFlag } from '@mews/framework';
import {
    CommanderFormProps,
    FormLanguageProvider,
    useDialogForm,
    useHandledDialogFormControls,
} from '@mews-commander/form';
import { RateType } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import { getSortedLanguages } from '@mews-commander/legacy-entities/src/ducks/languages';
import { useRateDetailSchema } from '../../schemas/rateDetailSchema';
import { useRateCreate } from '../../../mutations';
import { useCurrentServiceIdContext } from '../../contexts/CurrentServiceIdContext';
import { RateFormFields } from '../fields/RateFormFields';
import { transformDescriptions } from '../transformDescription';

export const useCreateRateDialogForm = () =>
    useDialogForm({
        id: 'rateCreateForm',
        renderForm: (props) => <CreateRateForm {...props} />,
        title: lt('CreateRate'),
    });

const CreateRateForm = ({ ...props }: CommanderFormProps) => {
    const rateDetailSchema = useRateDetailSchema();
    const rateDetailsFormResolver = createZodFormResolver(rateDetailSchema);
    const { defaultLanguageCode } = useCurrentEnterprise();
    const languages = useCommanderSelector(getSortedLanguages);

    const currentServiceId = useCurrentServiceIdContext();
    const isNordicChoiceRateOptionsEnabled = useFeatureFlag(FeatureFlag.nordicChoiceRateOptionsEnabled);
    const navigate = useEnterpriseNavigate();

    const { mutateAsync } = useRateCreate();

    const { renderFormContent } = useHandledDialogFormControls({
        ...props,
        defaultValues: {
            type: RateType.Public,
            ordering: 0,
            serviceId: currentServiceId,
            isDefault: false,
            isEnabled: false,
        },
        onSubmit: async ({
            names,
            shortNames,
            externalNames,
            descriptions,
            type,
            rateGroupId,
            accountingCategoryId,
            businessSegmentId,
            defaultReservationPurpose,
            pricingClassification,
            rateKey,
            ordering,
            isEnabled,
            baseRateId,
            isBonusPointsEligible,
            hidePriceFromGuest,
        }) => {
            await mutateAsync(
                {
                    serviceId: currentServiceId,
                    names,
                    shortNames,
                    descriptions: transformDescriptions(descriptions),
                    externalNames,
                    baseRateId,
                    type,
                    rateGroupId,
                    accountingCategoryId,
                    businessSegmentId,
                    defaultReservationPurpose: defaultReservationPurpose || null,
                    pricingClassification: pricingClassification || null,
                    ordering,
                    isEnabled,
                    rateKey,
                    ...(isNordicChoiceRateOptionsEnabled && {
                        options: {
                            hidePriceFromGuest,
                            isBonusPointsEligible,
                        },
                    }),
                },
                {
                    onSuccess: async (rate) => {
                        showSuccessAlert({ title: lt('RateCreatedSuccessMessage') });
                        await navigate({ to: 'Rate/Pricing/$rateId', params: { rateId: rate.id } });
                    },
                }
            );
        },
        resolver: rateDetailsFormResolver,
    });

    return renderFormContent(
        <FormLanguageProvider defaultLanguageCode={defaultLanguageCode} languages={languages} spacing="300">
            <RateFormFields isCreateForm />
        </FormLanguageProvider>
    );
};
